import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Medal } from 'lucide-react';
import { db } from '../../firebase/config';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { cn } from '../../lib/utils';
import { DEFAULT_MODE, getTournamentModes, modeLabel } from '../../constants/modes';
import { DEFAULT_POINTS_TABLE, DEFAULT_STAGE1_CONFIG } from '../Admin/shell/tournamentTools';
import Maps from './Maps';
import PlayersTab from './tournament/PlayersTab';
import RulesTab from './tournament/RulesTab';

const medalClass = { 1: 'text-gold', 2: 'text-silver', 3: 'text-bronze' };

const Placement = ({ placement }) => {
  if (!placement) return <span className="text-muted-foreground/40">—</span>;
  const top = placement <= 3;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-mono tabular-nums',
        top ? `${medalClass[placement]} font-semibold` : 'text-foreground/80'
      )}
    >
      {top && <Medal className="h-3.5 w-3.5" />}
      {placement}
    </span>
  );
};

const rankPlayers = (group, pointsTable) => {
  const maps = group.maps || [];
  return (group.players || [])
    .map((p) => {
      const totalPoints = maps.reduce((sum, map) => {
        const placement = map.rankings?.find((r) => r.playerId === p.id)?.placement;
        if (!placement || placement <= 0) return sum;
        return sum + (pointsTable[placement - 1] ?? 0);
      }, 0);
      return { ...p, totalPoints };
    })
    .sort((a, b) => b.totalPoints - a.totalPoints);
};

const GroupTable = ({ group, pointsTable, advanceTopN }) => {
  const maps = group.maps || [];
  const ranked = rankPlayers(group, pointsTable);
  return (
    <Card className="overflow-hidden border-border/60 bg-background/40">
      <CardHeader className="border-b border-border/60 pb-3">
        <CardTitle className="font-display text-lg">{group.name}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/60">
                <th className="w-14 px-3 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
                  Rank
                </th>
                <th className="px-3 py-2.5 text-left font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
                  Player
                </th>
                {maps.map((map) => (
                  <th
                    key={map.id}
                    className="min-w-[70px] px-3 py-2.5 text-center font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground"
                  >
                    {map.name}
                  </th>
                ))}
                <th className="w-20 px-3 py-2.5 text-center font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
                  Points
                </th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((player, index) => {
                const advancing = advanceTopN > 0 && index < advanceTopN;
                return (
                  <tr
                    key={player.id}
                    className={cn(
                      'border-b border-border/40 last:border-0',
                      advancing && 'bg-heat/5'
                    )}
                  >
                    <td className="px-3 py-2.5 font-mono text-xs tabular-nums text-muted-foreground">
                      {String(index + 1).padStart(2, '0')}
                    </td>
                    <td className="px-3 py-2.5 font-display text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <span>{player.name}</span>
                        {advancing && (
                          <Badge variant="outline" className="border-heat/60 text-heat">
                            Advancing
                          </Badge>
                        )}
                      </div>
                    </td>
                    {maps.map((map) => {
                      const placement = map.rankings?.find(
                        (r) => r.playerId === player.id
                      )?.placement;
                      return (
                        <td key={map.id} className="px-3 py-2.5 text-center">
                          <Placement placement={placement} />
                        </td>
                      );
                    })}
                    <td className="px-3 py-2.5 text-center font-mono text-sm font-semibold tabular-nums">
                      {player.totalPoints}
                    </td>
                  </tr>
                );
              })}
              {ranked.length === 0 && (
                <tr>
                  <td
                    colSpan={maps.length + 3}
                    className="py-6 text-center text-xs text-muted-foreground"
                  >
                    No players in this group.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const useGroupsByStage = (tournamentId, mode, stage) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, 'tournaments', tournamentId, 'groups'),
            where('mode', '==', mode),
            where('stage', '==', stage)
          )
        );
        if (!cancelled) setGroups(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [tournamentId, mode, stage]);

  return { groups, loading };
};

const SkeletonGrid = ({ columns }) => (
  <div className={cn('grid gap-5', columns === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2')}>
    {Array.from({ length: columns }).map((_, i) => (
      <div key={i} className="h-48 animate-pulse rounded-md border border-border/60 bg-muted/10" />
    ))}
  </div>
);

const Stage1View = ({ tournamentId, mode, pointsTable, advanceTopN }) => {
  const { groups, loading } = useGroupsByStage(tournamentId, mode, 1);

  if (loading) return <SkeletonGrid columns={2} />;
  if (groups.length === 0) {
    return (
      <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
        No groups published for this mode yet.
      </div>
    );
  }
  const sorted = [...groups].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      {sorted.map((g) => (
        <GroupTable key={g.id} group={g} pointsTable={pointsTable} advanceTopN={advanceTopN} />
      ))}
    </div>
  );
};

const Stage2View = ({ tournamentId, mode, pointsTable }) => {
  const { groups, loading } = useGroupsByStage(tournamentId, mode, 2);

  if (loading) return <SkeletonGrid columns={1} />;
  if (groups.length === 0) {
    return (
      <div className="border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
        Stage 2 begins after Stage 1 group play concludes. Advancing players will appear here.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-5">
      {groups.map((g) => (
        <GroupTable key={g.id} group={g} pointsTable={pointsTable} advanceTopN={0} />
      ))}
    </div>
  );
};

const GroupsFinalTournamentView = ({ tournament, players, tournamentId }) => {
  const tournamentModes = useMemo(() => getTournamentModes(tournament), [tournament]);
  const [mode, setMode] = useState(tournamentModes[0] || DEFAULT_MODE);

  useEffect(() => {
    if (tournamentModes.length > 0 && !tournamentModes.includes(mode)) {
      setMode(tournamentModes[0]);
    }
  }, [tournamentModes, mode]);

  const pointsTable = useMemo(
    () =>
      tournament.pointsTable && tournament.pointsTable.length > 0
        ? tournament.pointsTable
        : DEFAULT_POINTS_TABLE,
    [tournament.pointsTable]
  );
  const advanceTopN = tournament.stage1Config?.advanceTopN ?? DEFAULT_STAGE1_CONFIG.advanceTopN;

  const ModeSwitcher =
    tournamentModes.length > 1 ? (
      <div className="flex items-center justify-center">
        <div className="inline-flex rounded-md border border-border/60 bg-background/40 p-0.5">
          {tournamentModes.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                'px-4 py-1.5 font-mono text-xs font-medium uppercase tracking-[0.2em] transition-colors',
                mode === m
                  ? 'bg-secondary text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {modeLabel(m)}
            </button>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <Tabs defaultValue="stage1" className="w-full">
      <TabsList className="w-full justify-start overflow-x-auto">
        <TabsTrigger value="stage1">Stage 1 · Groups</TabsTrigger>
        <TabsTrigger value="stage2">Stage 2 · Finals</TabsTrigger>
        {!tournament.hideMaps && <TabsTrigger value="maps">Maps</TabsTrigger>}
        <TabsTrigger value="rules">Rules</TabsTrigger>
        <TabsTrigger value="players">Players</TabsTrigger>
      </TabsList>

      <TabsContent value="stage1" className="space-y-8">
        {ModeSwitcher}
        <section>
          <div className="mb-4 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            <span className="h-px w-6 bg-heat" />
            Group play — top {advanceTopN} advance
          </div>
          <Stage1View
            tournamentId={tournamentId}
            mode={mode}
            pointsTable={pointsTable}
            advanceTopN={advanceTopN}
          />
        </section>
      </TabsContent>

      <TabsContent value="stage2" className="space-y-8">
        {ModeSwitcher}
        <section>
          <div className="mb-4 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            <span className="h-px w-6 bg-heat" />
            Final group
          </div>
          <Stage2View tournamentId={tournamentId} mode={mode} pointsTable={pointsTable} />
        </section>
      </TabsContent>

      {!tournament.hideMaps && (
        <TabsContent value="maps">
          <Maps tournamentId={tournamentId} />
        </TabsContent>
      )}

      <TabsContent value="rules">
        <RulesTab html={tournament.rules} />
      </TabsContent>

      <TabsContent value="players">
        <PlayersTab players={players} />
      </TabsContent>
    </Tabs>
  );
};

export default GroupsFinalTournamentView;
